import { Box, Button, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { DIALOG_COLORS, DIALOG_FONT_FAMILY } from "./styles";

const ALWAYS_VISIBLE_LINES = ["visited_with", "requirement", "units_shown_liked", "concerns"];
const EXTRA_LINES = ["price_fit", "next_step"];
const CP_ONLY_EXTRA_LINES = ["ask_to_cp"];
const DOS = ["facts", "concerns", "next_step", "internal"];
const DONTS = ["discounts", "cp_conduct", "names", "opinions", "finance"];
const CP_ONLY_DONTS = ["cp_conduct"];

interface GuidelinesContentProps {
	isCp: boolean;
	expanded: boolean;
	// When not passed, everything is shown and there is no View all / Show less toggle
	onToggle?: () => void;
}

const bodyTextSx = { fontFamily: DIALOG_FONT_FAMILY, fontSize: 14, lineHeight: 1.45 };

const GuidelinesContent = ({ isCp, expanded, onToggle }: GuidelinesContentProps) => {
	const { t } = useTranslation();
	const base = "site_visit_comment.guidelines";

	const extraLines = isCp ? [...EXTRA_LINES, ...CP_ONLY_EXTRA_LINES] : EXTRA_LINES;
	const donts = DONTS.filter((key) => isCp || !CP_ONLY_DONTS.includes(key));

	const renderLines = (keys: string[]) => (
		<Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "180px 1fr" }, columnGap: 3, rowGap: { xs: 0.25, sm: 1 } }}>
			{keys.map((key) => (
				<Box key={key} sx={{ display: "contents" }}>
					<Typography sx={{ ...bodyTextSx, fontWeight: 700 }}>{t(`${base}.lines.${key}.label`)}</Typography>
					<Typography sx={{ ...bodyTextSx, color: DIALOG_COLORS.muted }}>
						{t(`${base}.lines.${key}.text`)}
						{key === "visited_with" && isCp && t(`${base}.lines.visited_with.cp_suffix`)}
						{key === "visited_with" && t(`${base}.lines.visited_with.language_suffix`)}
					</Typography>
				</Box>
			))}
		</Box>
	);

	const renderList = (title: string, keys: string[], group: "dos" | "donts", color: string) => (
		<Box sx={{ flex: 1, minWidth: 240 }}>
			<Typography
				sx={{ ...bodyTextSx, fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color }}
			>
				{title}
			</Typography>
			<Box component="ul" sx={{ m: 0, mt: 1, pl: 2.5 }}>
				{keys.map((key) => (
					<Typography component="li" key={key} sx={{ ...bodyTextSx, mb: 0.5 }}>
						{t(`${base}.${group}.${key}`)}
					</Typography>
				))}
			</Box>
		</Box>
	);

	return (
		<Box sx={{ border: `1px solid ${DIALOG_COLORS.border}`, borderRadius: "6px", mt: 2, overflow: "hidden" }}>
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					flexWrap: "wrap",
					gap: 1,
					px: 2,
					py: 1.25,
					backgroundColor: DIALOG_COLORS.surface,
				}}
			>
				<Typography
					sx={{ ...bodyTextSx, fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", color: DIALOG_COLORS.noteText }}
				>
					{t(`${base}.include_title`)}
				</Typography>
				<Typography sx={{ ...bodyTextSx, fontSize: 13, color: DIALOG_COLORS.muted }}>
					{t(`${base}.include_subtitle`)}
				</Typography>
			</Box>

			<Box sx={{ px: 2, py: 1.5 }}>{renderLines(ALWAYS_VISIBLE_LINES)}</Box>

			{expanded && (
				<>
					<Box sx={{ px: 2, py: 1.5, borderTop: `1px dashed ${DIALOG_COLORS.border}` }}>
						{renderLines(extraLines)}
					</Box>
					<Box
						sx={{
							display: "flex",
							flexWrap: "wrap",
							gap: 3,
							px: 2,
							py: 2,
							borderTop: `1px solid ${DIALOG_COLORS.border}`,
						}}
					>
						{renderList(t(`${base}.do_title`), DOS, "dos", DIALOG_COLORS.doHeading)}
						{renderList(t(`${base}.dont_title`), donts, "donts", DIALOG_COLORS.dontHeading)}
					</Box>
				</>
			)}

			{onToggle && (
				<Box sx={{ px: 2, pb: 1.5, pt: expanded ? 0 : 0.5 }}>
					<Button
						onClick={onToggle}
						size="small"
						disableRipple
						sx={{
							fontFamily: DIALOG_FONT_FAMILY,
							textTransform: "none",
							fontSize: 13,
							fontWeight: 500,
							p: 0,
							minWidth: 0,
							color: DIALOG_COLORS.link,
							"&:hover": { backgroundColor: "transparent", textDecoration: "underline" },
						}}
					>
						{expanded
							? `${t(`${base}.show_less`)} ▴`
							: `${t(isCp ? `${base}.view_all_cp` : `${base}.view_all_other`)} ▾`}
					</Button>
				</Box>
			)}
		</Box>
	);
};

export default GuidelinesContent;
